import React, { useEffect } from 'react';
import { ListElementProps } from "../button/AddListElementButton";
import CloseButton from "../button/CloseButton";
import SelectDropdown from "./SelectDropdown";
import { PayType } from "../enums/PayType";
import DateRange from "./DateRange";
import MoneyInput from "./MoneyInput";
import { useFormikContext } from "formik";
import { DisabilityEmployerFormValues } from "../disability/DisabilityEmployerFormValues";
import { PfmlEmployerFormValues } from "../pfml/PfmlEmployerFormValues";
import { calculateYearlyEarnings, findHourlyEquivalentFor, PayFrequency } from "../enums/PayFrequency";
import DecimalInput from "./DecimalInput";
import Tooltip from "./Tooltip";
import Checkbox from "./Checkbox";
import PercentageInput from "./PercentageInput";
import TwoRowDiv from "../structure/TwoRowDiv";
import CenteredFullWidthDiv from "../structure/CenteredFullWidthDiv";
import { DateTime } from "luxon";

var _ = require('lodash');

const Pay = (props: ListElementProps) => {
    const { values, setFieldValue } = useFormikContext<DisabilityEmployerFormValues | PfmlEmployerFormValues>();
    const { baseWage, baseWageFrequency, hoursWorkedPerWeek } = values as DisabilityEmployerFormValues;
    const { earningsQuarters } = values as PfmlEmployerFormValues;
    const namePrefix = _.camelCase(props.idPrefix);
    const index = props.index;
    const payArray = (values as any)[namePrefix] ?? [];
    const payObject = payArray[index ?? 0]
    const { payType, startDate, endDate, totalHoursPaid, percentagePaid, incorrectEstimate } = payObject ?? {};
    const showTotalHoursPaid = !!(payType && startDate && endDate);
    const totalHoursInPeriod = getTotalHours();
    const hasQuarterEarnings = earningsQuarters && !baseWage;
    const hourlyPay = findHourlyEquivalentFor(hasQuarterEarnings ? calculateYearlyEarnings(earningsQuarters) : baseWage, hoursWorkedPerWeek,
        hasQuarterEarnings ? PayFrequency.YEARLY : baseWageFrequency as PayFrequency);

    useEffect(() => {
        let estimatedAmount = 0;
        if (totalHoursPaid) {
            estimatedAmount = hourlyPay * totalHoursPaid
        } else if (percentagePaid) {
            estimatedAmount = (hourlyPay * totalHoursInPeriod) * (parseFloat(percentagePaid) / 100)
        }
        if (estimatedAmount) {
            setFieldValue(`${namePrefix}[${index}].estimateAmount`, estimatedAmount.toFixed(2))
        }
    }, [namePrefix, index, hourlyPay, percentagePaid, totalHoursPaid, totalHoursInPeriod, setFieldValue])

    return (
        <div>
            { props.closeCallback && <CloseButton idPrefix="" index={index} closeCallback={props.closeCallback}/>}
            <SelectDropdown id={props.idPrefix + "-pay-type-" + index} name={`${namePrefix}[${index}].payType`}
                            label="Pay type" required={true} options={
                Object.keys(PayType).map(p => ({
                    id: p.replace(/_/g, '-').toLowerCase(),
                    text: (PayType as any)[p]
                }))}/>
            <DateRange idPrefix={props.idPrefix} index={index} startLabel="Paid from" endLabel="Paid through" startRequired={true} endRequired={true}/>
            {showTotalHoursPaid &&
                <TwoRowDiv
                    topContent={<DecimalInput id={props.idPrefix + "-total-hours-paid-" + index} name={`${namePrefix}[${index}].totalHoursPaid`}
                                          label={`Total hours paid of ${payType.toLowerCase()} from ${startDate} to ${endDate}`}  required={false}
                                          disabled={percentagePaid > 0}/>}
                    middleContent={<CenteredFullWidthDiv divContent="or"/>}
                    bottomContent={<PercentageInput id={props.idPrefix + "-percentage-paid-" + index} name={`${namePrefix}[${index}].percentagePaid`}
                                             label={`Percentage paid of ${payType.toLowerCase()} from ${startDate} to ${endDate}`}  required={false}
                                             disabled={totalHoursPaid > 0}/>}
                />}
            {(totalHoursPaid || percentagePaid) && <div>
                <MoneyInput id={props.idPrefix + "-estimate-amount-" + index} name={`${namePrefix}[${index}].estimateAmount`}
                            label={`Estimated calculated amount paid`} required={false} disabled={true}
                            labelContent={<Tooltip text="The estimated amount has been pre-populated based on the wage information submitted in the previous step."/>}/>
                <Checkbox id={props.idPrefix + "-incorrect-estimate-" + index} name={`${namePrefix}[${index}].incorrectEstimate`} label={"Estimate amount is incorrect?"}/>
                {incorrectEstimate &&
                    <MoneyInput id={props.idPrefix + "-amount-" + index} name={`${namePrefix}[${index}].amount`} label="Amount" required={true}/>
                }
            </div>}
        </div>
    )

    function getTotalHours() {
        if (!startDate || !endDate) {
            return 0;
        }
        const totalDays = DateTime.fromFormat(endDate, "MM/dd/yyyy").diff(DateTime.fromFormat(startDate, "MM/dd/yyyy"), "days").days
        return (totalDays / 7) * parseFloat(hoursWorkedPerWeek);
    }
};

export default Pay;